import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import { Panel } from 'primereact/panel'
import { Button } from 'primereact/button'

import { InfoIcon } from '../../../../lib/icons/info-icon'
import Text from '../../../../ui/Text'
import { SDisabledText, SRedirectLink } from '../../../../ui/loadBalancer/LoadBalancer.styles'
import { Combobox } from '../../../components/combobox'
import InlineAlert, { AlertType } from '../../../components/InlineAlert'
import Input from '../../../components/Input'
import Toggle from '../../../components/Toggle'
import Tooltip from '../../../components/Tooltip'
import {
    EarthingSystem,
    LoadBalancerGroup,
    useCompanyGetDeviceByCompanyIdQuery,
    useLoadBalancerGroupUpdateMutation,
    MeasurementDevicesGetModel,
    MeasurementDeviceExtendedGetModel,
} from '../../../redux/api/web-api'
import { SFormRow, SLabelText } from '../../../styles/Form'
import { SActions } from '../../chargepoint-details-widget/chargepoint-details-widget.styles'
import { notifyError, notifySuccess } from '../../notificationsManager/Notify'
import { useLoadbalancerElectricalSettingsSchema } from '../load-balancer-group-form-validation'
import { Form } from '../loadbalancer-group-form/loadbalancer-group-form.styling'
import { SToggleTextInfo, transformLBCurrentInput } from '../loadbalancer-group-modal'
import { LoadbalancerPhaseSettingsWidget } from '../../../components/loadbalancer-phase-settings-widget'
import { Phases } from '../../../enums/phaseTypes'
import useText from '../../../hooks/useText'
import { LoadbalancerInfoModal } from '../loadbalancer-info-modal'

interface LoadBalancerElectricalInputsProps {
    loadBalancerGroup: LoadBalancerGroup
    hasChargers: boolean
    getMeasurementDevice: MeasurementDeviceExtendedGetModel | undefined
}

export function LoadBalancerElectricalInputs({
    loadBalancerGroup,
    hasChargers,
}: LoadBalancerElectricalInputsProps): React.ReactElement {
    const text = useText()

    const { data: deviceData } = useCompanyGetDeviceByCompanyIdQuery(
        { companyId: loadBalancerGroup?.companyId },
        { skip: !loadBalancerGroup?.companyId }
    )

    const [measurementDevice, setMeasurementDevice] = useState(loadBalancerGroup?.measurementDeviceId)
    const [schukoEnabled, setSchukoEnabled] = useState<boolean>(
        (loadBalancerGroup && loadBalancerGroup.schukoEnabled) ?? false
    )
    const [allowedPhases, setAllowedPhases] = useState<Phases | null | undefined>(null)

    const [phasesValid, setPhasesValid] = useState<boolean>(false)
    const [enableSave, setEnableSave] = useState<boolean>(false)
    const [openInfoModal, setOpenInfoModal] = useState(false)
    const [earthingScheme, setEarthingScheme] = useState<EarthingSystem | null | undefined>(
        loadBalancerGroup?.earthingSystem
    )
    const [chargePointsIncluded, setChargePointsIncluded] = useState<boolean>(
        loadBalancerGroup?.chargePointsIncludedInMeasurement ?? false
    )
    const showInfo = true

    const loadbalancerElectricalSettingsSchema = useLoadbalancerElectricalSettingsSchema()

    const {
        register,
        setValue,
        handleSubmit,
        trigger,
        reset,
        watch,
        control,
        formState: { errors, isValid, isDirty, isSubmitSuccessful },
    } = useForm<LoadBalancerGroup>({
        resolver: yupResolver(loadbalancerElectricalSettingsSchema(!!measurementDevice)),
        defaultValues: {
            supplyMaxCurrent: loadBalancerGroup?.supplyMaxCurrent,
            maxCurrentPerPhase: loadBalancerGroup?.maxCurrentPerPhase,
            minCurrentPerChargePoint: loadBalancerGroup?.minCurrentPerChargePoint,
            deviceControlledMaxCurrentPerPhase: loadBalancerGroup?.deviceControlledMaxCurrentPerPhase,
            earthingSystem: loadBalancerGroup?.earthingSystem,
            allowedPhases: loadBalancerGroup?.allowedPhases,
        },
        mode: 'all',
    })

    const [updateLoadBalancerGroup, { isLoading: groupLoading }] = useLoadBalancerGroupUpdateMutation()

    const saveLbgroup = async (formData: LoadBalancerGroup) => {
        const tempLoadBalancerGroup = {
            ...loadBalancerGroup,
            ...formData,
            schukoEnabled: schukoEnabled,
            allowedPhases: allowedPhases ? allowedPhases : loadBalancerGroup?.allowedPhases,
            measurementDeviceId: measurementDevice ? measurementDevice : formData?.measurementDeviceId,
            chargePointsIncludedInMeasurement: chargePointsIncluded,
            earthingSystem: earthingScheme,
        }
        try {
            await updateLoadBalancerGroup({
                loadBalancerGroup: tempLoadBalancerGroup,
            }).unwrap()
            notifySuccess(text('msgLoadbalancerGroupUpdate'))
        } catch (error: {
            error: string
            messageCode: string | null
            errorData: Array<{ [k: string]: string }>
        }) {
            if (error.data.messageCode == 'msgMeasurementDeviceInUse') {
                notifyError(text('msgMeasurementDeviceInUse'))
            } else {
                notifyError(text('msgLoadbalancerGroupUpdateError'))
            }
        }
    }

    useEffect(() => {
        setMeasurementDevice(loadBalancerGroup?.measurementDeviceId)
    }, [loadBalancerGroup?.measurementDeviceId, setMeasurementDevice])

    useEffect(() => {
        if (isSubmitSuccessful) {
            reset({}, { keepValues: true })
            setEnableSave(false)
        }
    }, [isSubmitSuccessful, reset])

    const minCurrent = watch('minCurrentPerChargePoint')
    useEffect(() => {
        if (minCurrent) {
            trigger('maxCurrentPerPhase')
            trigger('deviceControlledMaxCurrentPerPhase')
        }
        if (measurementDevice) {
            {
                trigger('maxCurrentPerPhase')
                trigger('deviceControlledMaxCurrentPerPhase')
                trigger('supplyMaxCurrent')
            }
        }
    }, [minCurrent, measurementDevice, trigger])

    const disabledPhases = !enableSave || !phasesValid
    const disabledSettings = groupLoading || !isDirty || !isValid
    const disabledSaveButton = (disabledPhases && disabledSettings) || !isValid || !phasesValid

    return (
        <Panel
            header={
                <div className="flex">
                    {text('lblElectricalSettings')}

                    <Button
                        className="ml-2 p-0 pb-1 pl-1 "
                        text
                        severity="info"
                        onClick={() => {
                            setOpenInfoModal(true)
                        }}
                    >
                        {text('btnViewInfographics')}
                    </Button>
                </div>
            }
            data-testid="loadbalancer-electrical-settings"
        >
            {loadBalancerGroup?.active && (
                <InlineAlert
                    text={text('msgLoadBalancerSettingsEdit')}
                    alertType={AlertType.Warning}
                    data-testid="edit-msg"
                />
            )}

            <Form name="form" role="form" noValidate className="ml-4 mr-4">
                <SFormRow>
                    <span>
                        <LoadbalancerPhaseSettingsWidget
                            earthingScheme={earthingScheme}
                            setEarthingScheme={setEarthingScheme}
                            loadbalancerGroup={loadBalancerGroup}
                            hasChargers={hasChargers}
                            showInfo={showInfo}
                            classNameSubHeading={'xl:w-12'}
                            setEnableSave={setEnableSave}
                            setAllowedPhases={setAllowedPhases}
                            setPhasesValid={setPhasesValid}
                        />
                    </span>
                </SFormRow>

                {!earthingScheme && (
                    <SFormRow>
                        <div className="flex justify-content-between">
                            <SLabelText required={true}>
                                <Text code="lblPhases" />
                            </SLabelText>
                            <span className="mt-3">
                                <InfoIcon
                                    data-testid="allowedPhases"
                                    title={text('msgInstallationWizardAddLBPhasesTooltip')}
                                    size={22}
                                />
                            </span>
                        </div>

                        <Controller
                            name="allowedPhases"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <Input
                                    id="allowedPhases"
                                    data-testid="allowedPhases-input"
                                    {...register('allowedPhases')}
                                    disabled={loadBalancerGroup?.active}
                                    onChange={(e) => {
                                        setAllowedPhases(e.target.value)
                                        onChange(e.target.value)
                                    }}
                                    value={value}
                                />
                            )}
                        />
                    </SFormRow>
                )}
                <SFormRow>
                    <div className="flex justify-content-between ">
                        <SLabelText required={true}>
                            <Text code="lblMinCurrentPerCharger" />
                        </SLabelText>
                        <span className="mt-3">
                            <InfoIcon
                                title={text('msgInstallationWizardAddLBMinCurrentPerChargerTooltip')}
                                size={22}
                                data-testid="minCurrentPerCharger-tooltip"
                            />
                        </span>
                    </div>
                    <Controller
                        name="minCurrentPerChargePoint"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Input
                                required
                                id="minCurrentPerCharger"
                                data-testid="MinCurrentPerCharger-input"
                                {...register('minCurrentPerChargePoint')}
                                error={errors.minCurrentPerChargePoint?.message}
                                type="number"
                                onChange={(e) => {
                                    const transformedValue = parseInt(e.target.value)
                                    onChange(transformLBCurrentInput.input(transformedValue.toString()))
                                }}
                                value={value}
                                disabled={loadBalancerGroup?.active}
                            ></Input>
                        )}
                    />
                </SFormRow>
                <SFormRow>
                    <div className="flex justify-content-between ">
                        <SLabelText>
                            <Text code="lblMeasurementDevice" />
                        </SLabelText>
                        <span className="mt-3">
                            <InfoIcon data-testid="add-md" title={text('msgAddMDToLB')} size={22} className="mt-2" />
                        </span>
                    </div>
                    <div
                        data-testid="device-redirect"
                        className={classNames({ 'flex justify-content-between ': measurementDevice })}
                    >
                        <span className="w-9 xl:w-10">
                            <Controller
                                name="measurementDeviceId"
                                control={control}
                                render={({ field: { onChange } }) => (
                                    <Combobox
                                        id="measurementDevice"
                                        data-testid="measurement-device-input"
                                        options={deviceData
                                            ?.filter(
                                                (device) =>
                                                    !device?.loadBalancerGroupId ||
                                                    device?.loadBalancerGroupId === loadBalancerGroup?.id
                                            )
                                            .map((device: MeasurementDevicesGetModel) => ({
                                                value: device?.id,
                                                label: device?.name || device?.serialNumber,
                                            }))}
                                        value={measurementDevice}
                                        enableDefaultOption={true}
                                        {...register('measurementDeviceId')}
                                        onChange={(option) => {
                                            onChange(option.value)
                                            setMeasurementDevice(option.value)
                                            setValue('measurementDeviceId', option?.value)
                                        }}
                                        enableDefaultTextOption={
                                            deviceData && deviceData.length > 0
                                                ? text('lblSelect')
                                                : text('lblmeasurementdeviceNotAvailable')
                                        }
                                        disabled={loadBalancerGroup?.active}
                                    />
                                )}
                            />
                        </span>
                        {measurementDevice && (
                            <span className="mt-3">
                                {isDirty ? (
                                    <SDisabledText>
                                        <Text code="lblGoTo" />
                                    </SDisabledText>
                                ) : (
                                    <SRedirectLink
                                        as={Link}
                                        to={`/measurementDevice/${loadBalancerGroup?.measurementDeviceId}`}
                                        data-testid="go-to-measurement-device"
                                        title={text('btnGoToMD')}
                                    >
                                        <Text code="lblGoTo" />
                                    </SRedirectLink>
                                )}
                            </span>
                        )}
                    </div>
                </SFormRow>
                {measurementDevice && (
                    <SFormRow>
                        <div className="flex justify-content-between ">
                            <SLabelText required={true}>
                                <Text code="lblSupplyMaxCurrent" />
                            </SLabelText>
                            <span className="mt-3">
                                <InfoIcon
                                    dataTestId="supplyMaxCurrent"
                                    title={text('msgInstallationWizardConfigSupplyCurrentTooltip')}
                                    size={22}
                                />
                            </span>
                        </div>
                        <Controller
                            name="supplyMaxCurrent"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <Input
                                    id="supplyMaxCurrentMeasurementDeviceField"
                                    required
                                    data-testid="supplyMaxCurrentMeasurementDeviceField-input"
                                    {...register('supplyMaxCurrent')}
                                    error={errors.supplyMaxCurrent?.message}
                                    type="number"
                                    onChange={(e) => {
                                        const transformedValue = parseInt(e.target.value)
                                        onChange(transformLBCurrentInput.input(transformedValue.toString()))
                                    }}
                                    value={value ?? 0}
                                    disabled={loadBalancerGroup?.active}
                                ></Input>
                            )}
                        />
                    </SFormRow>
                )}

                <SFormRow>
                    <div className="flex justify-content-between ">
                        <SLabelText required={true}>
                            {measurementDevice
                                ? text('lblMaxCurrentPerPhaseAGOffline')
                                : text('lblInstallationWizardMaxCurrentPerPhase')}
                        </SLabelText>
                        <span className="mt-3">
                            <InfoIcon
                                dataTestId="maxCurrentperPhase"
                                title={
                                    measurementDevice
                                        ? text('msgInstallationWizardConfigLoadbalancerMaxCurrentOfflineTooltip')
                                        : text('msgInstallationWizardAddLBMaxCurrentPerPhaseTooltip')
                                }
                                size={22}
                            />
                        </span>
                    </div>
                    <Controller
                        name="maxCurrentPerPhase"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Input
                                id="installationWizardMaxCurrentPerPhase"
                                required
                                data-testid="installationWizardMaxCurrentPerPhase-input"
                                {...register('maxCurrentPerPhase')}
                                error={errors.maxCurrentPerPhase?.message}
                                type="number"
                                onChange={(e) => {
                                    const transformedValue = parseInt(e.target.value)
                                    onChange(transformLBCurrentInput.input(transformedValue.toString()))
                                }}
                                value={value}
                                disabled={loadBalancerGroup?.active}
                            ></Input>
                        )}
                    />
                </SFormRow>

                {measurementDevice && (
                    <SFormRow>
                        <div className="flex justify-content-between ">
                            <SLabelText required={true}>
                                <Text code="lblMaxCurrentPerPhaseAGOnline" />
                            </SLabelText>
                            <span className="mt-3">
                                <InfoIcon
                                    data-testid="maxCurrentPerPhaseAGOnlineInfo"
                                    title={text('msgInstallationWizardConfigLoadbalancerMaxCurrentOnlineTooltip')}
                                    size={22}
                                />
                            </span>
                        </div>
                        <Controller
                            name="deviceControlledMaxCurrentPerPhase"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <Input
                                    id="maxCurrentPerPhaseAGOnline"
                                    required
                                    data-testid="maxCurrentPerPhaseAGGOnline-input"
                                    {...register('deviceControlledMaxCurrentPerPhase')}
                                    error={errors.deviceControlledMaxCurrentPerPhase?.message}
                                    type="number"
                                    onChange={(e) => {
                                        const transformedValue = parseInt(e.target.value)
                                        onChange(transformLBCurrentInput.input(transformedValue.toString()))
                                    }}
                                    value={value ?? 0}
                                    disabled={loadBalancerGroup?.active}
                                ></Input>
                            )}
                        />
                    </SFormRow>
                )}
                <SFormRow>
                    <Controller
                        name="measurementDeviceId"
                        control={control}
                        render={({ field: { onChange } }) => (
                            <Toggle
                                {...register('schukoEnabled')}
                                size="md"
                                data-testid="socket-enabled-toggle"
                                isToggled={schukoEnabled}
                                toggleFn={() => onChange(setSchukoEnabled((schukoEnabled) => !schukoEnabled))}
                                toggleText={text('lblSocketEnabled')}
                                labelPos="right"
                                disabled={loadBalancerGroup?.active}
                            ></Toggle>
                        )}
                    />
                </SFormRow>
                <SFormRow>
                    {measurementDevice && (
                        <SToggleTextInfo>
                            <Controller
                                name="measurementDeviceId"
                                control={control}
                                render={({ field: { onChange } }) => (
                                    <Toggle
                                        {...register('chargePointsIncludedInMeasurement')}
                                        size="md"
                                        data-testid="chargepoints-included-toggle"
                                        isToggled={chargePointsIncluded}
                                        toggleFn={() =>
                                            onChange(
                                                setChargePointsIncluded((chargePointsIncluded) => !chargePointsIncluded)
                                            )
                                        }
                                        toggleText={text('lblChargersIncludedInPowerConsumption')}
                                        labelPos="right"
                                        disabled={loadBalancerGroup?.active}
                                    ></Toggle>
                                )}
                            />

                            <span className="margin-top-6">
                                <Tooltip content={'lblChargersIncludedInPowerConsumptionInfo'}>
                                    <InfoIcon id="chargersincluded-info-icon" size={22} />
                                </Tooltip>
                            </span>
                        </SToggleTextInfo>
                    )}
                </SFormRow>

                <SActions>
                    <Button
                        data-testid="save-button"
                        label={text('btnSave')}
                        disabled={disabledSaveButton || loadBalancerGroup.active}
                        onClick={handleSubmit(saveLbgroup)}
                        loading={groupLoading}
                        type="submit"
                    />
                </SActions>
            </Form>
            {openInfoModal && (
                <LoadbalancerInfoModal
                    openInfoModal={openInfoModal}
                    close={() => setOpenInfoModal(false)}
                    measurementDeviceId={measurementDevice}
                />
            )}
        </Panel>
    )
}
